import { useId } from 'react';
import { PRODUCT_KEYS, type OrgRole, type ProductKey } from '@traceio-org/platform-contracts';
import { productName } from '../format';

/** What each organization role may do, in a sentence for the role choices. */
export const ROLE_DESCRIPTIONS: Readonly<Record<OrgRole, string>> = {
  org_owner: 'Everything an administrator can do, plus billing changes.',
  org_admin: 'Manages members, products, modules and domains.',
  billing_admin: 'Reads and changes billing only.',
  member: 'Uses the products granted to them.',
  help_desk: 'Answers and manages support tickets. Nothing administrative.',
};

/** Products a person of an organization can be granted: the employee products, never Trace Admin. */
export const EMPLOYEE_PRODUCTS: readonly ProductKey[] = PRODUCT_KEYS.filter(
  (key) => key !== 'admin' && key !== 'portal',
);

/** The product grants of the edit form: which products, and the product role of each (free text). */
export function ProductGrantsField({
  candidates,
  grants,
  onChange,
}: {
  candidates: readonly ProductKey[];
  grants: Readonly<Partial<Record<ProductKey, string>>>;
  onChange: (grants: Partial<Record<ProductKey, string>>) => void;
}) {
  const id = useId();
  return (
    <fieldset className="admin-choices">
      <legend className="admin-field__label">Products</legend>
      <p className="admin-field__hint">
        A product role is the product&apos;s own word for the job, for example operations. Leave it empty for
        none.
      </p>
      {candidates.map((key) => {
        const checked = grants[key] !== undefined;
        const boxId = `${id}-${key}`;
        return (
          <div className="admin-choice admin-choice--with-field" key={key}>
            <input
              id={boxId}
              type="checkbox"
              checked={checked}
              onChange={(event) => {
                const next = { ...grants };
                if (event.currentTarget.checked) next[key] = '';
                else delete next[key];
                onChange(next);
              }}
            />
            <label htmlFor={boxId}>{productName(key)}</label>
            {checked ? (
              <input
                className="admin-input admin-input--inline"
                type="text"
                aria-label={`Product role in ${productName(key)}`}
                placeholder="Product role"
                maxLength={100}
                value={grants[key] ?? ''}
                onChange={(event) => onChange({ ...grants, [key]: event.currentTarget.value })}
              />
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}
